(() => {
  'use strict';

  if (window.__refreshTermsGateLoaded) return;
  window.__refreshTermsGateLoaded = true;

  const configElement = document.getElementById('TermsGateConfig');
  let themeConfig = {};

  try {
    themeConfig = configElement ? JSON.parse(configElement.textContent) : {};
  } catch (error) {
    console.error('[Terms Gate] Hibás konfiguráció.', error);
  }

  const config = {
    cartUrl: '/cart.js',
    cartUpdateUrl: '/cart/update.js',
    cartPageUrl: '/cart',
    initialAccepted: false,
    savingText: 'Elfogadás mentése…',
    requiredMessage: 'A folytatáshoz el kell fogadnod a Felhasználási feltételeket.',
    syncErrorMessage: 'Az elfogadást most nem sikerült menteni. Ellenőrizd az internetkapcsolatot, majd próbáld újra.',
    ...themeConfig,
  };

  const ATTRIBUTE_KEY = 'terms_gate_accepted';
  const ATTRIBUTE_TIME_KEY = 'terms_gate_accepted_at';
  const ADD_FORM_SELECTOR = 'form[action*="/cart/add"]';
  const CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    '.cart__checkout-button',
    '[data-terms-checkout]',
    'a[href*="/checkout"]',
    'shopify-accelerated-checkout-cart',
    '.additional-checkout-buttons',
  ].join(',');
  const PRODUCT_PAYMENT_SELECTOR = '.shopify-payment-button, shopify-accelerated-checkout';

  const originalFetch = window.fetch.bind(window);
  const productRootByForm = new WeakMap();
  const addIntents = [];
  const state = {
    accepted: Boolean(config.initialAccepted),
    initialized: false,
    syncing: true,
    pendingAcceptance: null,
  };

  let syncQueue = Promise.resolve();
  let hydratePromise;
  let discoveryQueued = false;

  function isElement(value) {
    return value instanceof Element;
  }

  function pathOf(value) {
    try {
      return new URL(value, window.location.origin).pathname.replace(/\/+$/, '');
    } catch (_) {
      return '';
    }
  }

  function isCartPage() {
    return pathOf(window.location.href) === pathOf(config.cartPageUrl);
  }

  function isAddUrl(value) {
    return /\/cart\/add(?:\.js)?$/.test(pathOf(value));
  }

  function isCartUpdateUrl(value) {
    return /\/cart\/update(?:\.js)?$/.test(pathOf(value));
  }

  function requestUrl(input) {
    return typeof input === 'string' || input instanceof URL ? String(input) : input?.url || '';
  }

  function requestMethod(input, options) {
    return String(options?.method || input?.method || 'GET').toUpperCase();
  }

  function isAddRequest(input, options) {
    return requestMethod(input, options) !== 'GET' && isAddUrl(requestUrl(input));
  }

  function isAddForm(form) {
    return form instanceof HTMLFormElement && isAddUrl(form.getAttribute('action') || form.action || '');
  }

  function effectiveAcceptance() {
    return state.syncing && state.pendingAcceptance !== null
      ? state.pendingAcceptance
      : state.accepted;
  }

  function setRootError(root, message) {
    if (!root) return;
    const error = root.querySelector('[data-terms-error]');
    root.classList.add('terms-gate--invalid');
    if (error) {
      error.textContent = message || root.dataset.errorMessage || config.requiredMessage;
      error.hidden = false;
    }
  }

  function clearRootError(root) {
    if (!root) return;
    root.classList.remove('terms-gate--invalid');
    const error = root.querySelector('[data-terms-error]');
    if (error) error.hidden = true;
  }

  function clearCartErrors() {
    document.querySelectorAll('[data-terms-cart]').forEach(clearRootError);
  }

  function setProductBusy(root, busy) {
    if (!root) return;
    root.dataset.termsGateBusy = String(busy);
    root.setAttribute('aria-busy', String(busy));
    const input = root.querySelector('[data-terms-checkbox]');
    const status = root.querySelector('[data-terms-status]');
    if (input) input.disabled = busy;
    if (status) {
      status.textContent = busy ? config.savingText : '';
      status.hidden = !busy;
    }
    updateProductPaymentControls(root);
  }

  function setSubmitterBusy(submitter, busy) {
    if (!(submitter instanceof HTMLButtonElement) && !(submitter instanceof HTMLInputElement)) return;

    if (busy) {
      if (!submitter.disabled) {
        submitter.dataset.termsGateDisabled = 'true';
        submitter.disabled = true;
      }
      return;
    }

    if (submitter.dataset.termsGateDisabled === 'true') {
      submitter.disabled = false;
      delete submitter.dataset.termsGateDisabled;
    }
  }

  function getCartSurface(element) {
    if (!isElement(element)) return null;

    const drawer = element.closest('cart-drawer, #CartDrawer, .cart-drawer');
    if (drawer) return drawer;

    if (isCartPage()) return document.getElementById('MainContent') || document.querySelector('main') || document.body;

    return element.closest('#main-cart-footer, .cart__footer, form[action*="/cart"]');
  }

  function checkoutControls() {
    return Array.from(document.querySelectorAll(CHECKOUT_SELECTOR)).filter((control) => {
      if (!getCartSurface(control)) return false;
      if (control.matches('a[href*="/checkout"]')) return /\/checkout(?:\/|$)/.test(pathOf(control.href));
      return true;
    });
  }

  function updateCheckoutControls() {
    const blocked = state.syncing || !state.accepted;

    checkoutControls().forEach((control) => {
      control.classList.toggle('terms-gate-checkout-disabled', blocked);
      if (blocked) {
        control.setAttribute('aria-disabled', 'true');
        control.dataset.termsGateBlocked = 'true';
      } else if (control.dataset.termsGateBlocked === 'true') {
        control.removeAttribute('aria-disabled');
        delete control.dataset.termsGateBlocked;
      }
    });
  }

  function updateProductPaymentControls(singleRoot = null) {
    const roots = singleRoot ? [singleRoot] : Array.from(document.querySelectorAll('[data-terms-product]'));

    roots.forEach((root) => {
      if (root.closest('quick-add-modal')) return;
      const form = root.__termsGateForm;
      const input = root.querySelector('[data-terms-checkbox]');
      if (!form || !input) return;

      const blocked = !input.checked || root.dataset.termsGateBusy === 'true';
      form.querySelectorAll(PRODUCT_PAYMENT_SELECTOR).forEach((control) => {
        control.classList.toggle('terms-gate-payment-disabled', blocked);
        control.setAttribute('aria-disabled', String(blocked));
      });
    });
  }

  function renderState() {
    const shownAcceptance = effectiveAcceptance();

    document.querySelectorAll('[data-terms-cart]').forEach((root) => {
      const input = root.querySelector('[data-terms-checkbox]');
      const status = root.querySelector('[data-terms-status]');
      if (input) {
        input.checked = shownAcceptance;
        input.disabled = state.syncing;
      }
      root.setAttribute('aria-busy', String(state.syncing));
      if (status) {
        status.textContent = state.syncing ? config.savingText : '';
        status.hidden = !state.syncing;
      }
    });

    updateCheckoutControls();
    updateProductPaymentControls();
  }

  function dispatchAcceptanceChange(source) {
    document.dispatchEvent(
      new CustomEvent('terms-gate:change', {
        detail: { accepted: state.accepted, source },
      })
    );
  }

  async function readJson(response) {
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.description || data?.message || `HTTP ${response.status}`);
    }
    return data;
  }

  async function hydrateCart() {
    try {
      const response = await originalFetch(config.cartUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const cart = await readJson(response);
      state.accepted = cart?.attributes?.[ATTRIBUTE_KEY] === 'true';
    } catch (error) {
      console.warn('[Terms Gate] A kosárállapot nem olvasható, a szerveroldali kezdőállapotot használjuk.', error);
    } finally {
      state.initialized = true;
      state.syncing = false;
      state.pendingAcceptance = null;
      renderState();
    }
  }

  function persistAcceptance(target, source = 'unknown') {
    const desired = Boolean(target);
    const operation = syncQueue.then(async () => {
      await hydratePromise;
      const previous = state.accepted;
      state.syncing = true;
      state.pendingAcceptance = desired;
      renderState();

      try {
        const response = await originalFetch(config.cartUpdateUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attributes: {
              [ATTRIBUTE_KEY]: desired ? 'true' : '',
              [ATTRIBUTE_TIME_KEY]: desired ? new Date().toISOString() : '',
            },
          }),
        });
        const cart = await readJson(response);
        const persisted = cart?.attributes?.[ATTRIBUTE_KEY] === 'true';

        if (persisted !== desired) throw new Error('A Shopify nem a kért elfogadási állapotot mentette.');

        state.accepted = persisted;
        dispatchAcceptanceChange(source);
        return cart;
      } catch (error) {
        state.accepted = previous;
        throw error;
      } finally {
        state.syncing = false;
        state.pendingAcceptance = null;
        renderState();
      }
    });

    syncQueue = operation.catch(() => undefined);
    return operation;
  }

  async function ensureAcceptance(target, source) {
    await hydratePromise;
    const desired = Boolean(target);
    if (!state.syncing && state.accepted === desired) return null;
    return persistAcceptance(desired, source);
  }

  function resolveProductForm(root) {
    const selector = root.dataset.productFormSelector?.trim();
    if (selector) {
      try {
        const form = document.querySelector(selector);
        if (form instanceof HTMLFormElement && isAddForm(form)) return form;
      } catch (error) {
        console.warn('[Terms Gate] Érvénytelen termékűrlap-szelektor:', selector, error);
      }
    }

    const productInfo = root.closest('[id^="ProductInfo-"]');
    const localForm = productInfo?.querySelector(ADD_FORM_SELECTOR);
    return localForm instanceof HTMLFormElement ? localForm : null;
  }

  function initProductRoots() {
    document.querySelectorAll('[data-terms-product]').forEach((root) => {
      if (root.closest('quick-add-modal')) return;
      if (root.__termsGateForm?.isConnected) return;

      const form = resolveProductForm(root);
      if (!form) return;

      root.__termsGateForm = form;
      productRootByForm.set(form, root);

      const input = root.querySelector('[data-terms-checkbox]');
      if (input && input.dataset.termsGateBound !== 'true') {
        input.dataset.termsGateBound = 'true';
        input.addEventListener('change', () => {
          if (input.checked) clearRootError(root);
          updateProductPaymentControls(root);
        });
      }

      updateProductPaymentControls(root);
    });
  }

  function initCartRoots() {
    document.querySelectorAll('[data-terms-cart]').forEach((root) => {
      const input = root.querySelector('[data-terms-checkbox]');
      if (!input || input.dataset.termsGateBound === 'true') return;

      input.dataset.termsGateBound = 'true';
      input.addEventListener('change', async () => {
        const desired = input.checked;
        clearCartErrors();
        try {
          await persistAcceptance(desired, 'cart-checkbox');
        } catch (error) {
          console.error('[Terms Gate] Az elfogadás mentése sikertelen.', error);
          setRootError(root, config.syncErrorMessage);
          input.focus({ preventScroll: true });
        }
      });
    });
  }

  function discoverUi() {
    initProductRoots();
    initCartRoots();
    renderState();
  }

  function queueDiscovery() {
    if (discoveryQueued) return;
    discoveryQueued = true;
    requestAnimationFrame(() => {
      discoveryQueued = false;
      discoverUi();
    });
  }

  function enqueueAddIntent(accepted, previousAcceptance) {
    const intent = {
      accepted: Boolean(accepted),
      previousAcceptance: Boolean(previousAcceptance),
      expiresAt: Date.now() + 4000,
    };
    addIntents.push(intent);
    setTimeout(() => {
      const index = addIntents.indexOf(intent);
      if (index >= 0) addIntents.splice(index, 1);
    }, 4100);
  }

  function consumeAddIntent() {
    const now = Date.now();
    while (addIntents.length && addIntents[0].expiresAt < now) addIntents.shift();
    return addIntents.shift() || null;
  }

  async function replayAddForm(form, submitter, desired, productRoot) {
    const previousAcceptance = state.accepted;
    setProductBusy(productRoot, true);
    setSubmitterBusy(submitter, true);

    try {
      await ensureAcceptance(desired, desired ? 'product-add' : 'quick-add');
      enqueueAddIntent(desired, previousAcceptance);
      form.dataset.termsGateReplay = 'true';
      setProductBusy(productRoot, false);
      setSubmitterBusy(submitter, false);

      try {
        if (typeof form.requestSubmit === 'function') {
          const usableSubmitter = submitter?.form === form && !submitter.disabled ? submitter : undefined;
          form.requestSubmit(usableSubmitter);
        } else {
          HTMLFormElement.prototype.submit.call(form);
        }
      } catch (_) {
        delete form.dataset.termsGateReplay;
        HTMLFormElement.prototype.submit.call(form);
      }
    } catch (error) {
      setProductBusy(productRoot, false);
      setSubmitterBusy(submitter, false);
      console.error('[Terms Gate] A kosárba helyezés előtti mentés sikertelen.', error);

      if (productRoot) {
        setRootError(productRoot, config.syncErrorMessage);
        productRoot.querySelector('[data-terms-checkbox]')?.focus({ preventScroll: true });
      } else {
        const cartRoot = document.querySelector('[data-terms-cart]');
        if (cartRoot) setRootError(cartRoot, config.syncErrorMessage);
        else window.alert(config.syncErrorMessage);
      }
    } finally {
      delete form.dataset.termsGateProcessing;
    }
  }

  function getCheckoutControlFromEvent(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (const node of path) {
      if (isElement(node) && node.matches?.(CHECKOUT_SELECTOR) && getCartSurface(node)) return node;
    }
    const target = isElement(event.target) ? event.target.closest(CHECKOUT_SELECTOR) : null;
    return target && getCartSurface(target) ? target : null;
  }

  function showCheckoutError(control) {
    const surface = getCartSurface(control);
    const root = surface?.querySelector('[data-terms-cart]') || document.querySelector('[data-terms-cart]');
    if (!root) return;

    setRootError(root, state.syncing ? config.savingText : root.dataset.errorMessage || config.requiredMessage);
    root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => root.querySelector('[data-terms-checkbox]')?.focus(), 250);
  }

  function getProductPaymentFromEvent(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (const node of path) {
      if (isElement(node) && node.matches?.(PRODUCT_PAYMENT_SELECTOR)) return node;
    }
    return isElement(event.target) ? event.target.closest(PRODUCT_PAYMENT_SELECTOR) : null;
  }

  document.addEventListener(
    'click',
    (event) => {
      const checkoutControl = getCheckoutControlFromEvent(event);
      if (checkoutControl && (state.syncing || !state.accepted)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showCheckoutError(checkoutControl);
        return;
      }

      const paymentControl = getProductPaymentFromEvent(event);
      const paymentForm = paymentControl?.closest('form');
      const productRoot = paymentForm ? productRootByForm.get(paymentForm) : null;
      const productCheckbox = productRoot?.querySelector('[data-terms-checkbox]');
      if (productRoot && !productCheckbox?.checked) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setRootError(productRoot, productRoot.dataset.errorMessage || config.requiredMessage);
        productRoot.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => productCheckbox?.focus(), 250);
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const checkoutSubmitter = event.submitter && isElement(event.submitter)
        ? event.submitter.closest(CHECKOUT_SELECTOR)
        : null;
      if (checkoutSubmitter && getCartSurface(checkoutSubmitter) && (state.syncing || !state.accepted)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showCheckoutError(checkoutSubmitter);
        return;
      }

      if (!isAddForm(form)) return;

      if (form.dataset.termsGateReplay === 'true') {
        delete form.dataset.termsGateReplay;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      if (form.dataset.termsGateProcessing === 'true') return;

      initProductRoots();
      const productRoot = productRootByForm.get(form) || null;
      const productCheckbox = productRoot?.querySelector('[data-terms-checkbox]');
      const desired = Boolean(productRoot && productCheckbox?.checked);

      if (productRoot && !desired) {
        setRootError(productRoot, productRoot.dataset.errorMessage || config.requiredMessage);
        productCheckbox?.focus();
        productRoot.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      form.dataset.termsGateProcessing = 'true';
      replayAddForm(form, event.submitter, desired, productRoot);
    },
    true
  );

  function prepareBulkUpdate(options) {
    if (typeof options?.body !== 'string') return null;
    try {
      const payload = JSON.parse(options.body);
      if (!payload || typeof payload.updates !== 'object') return null;
      payload.attributes = {
        ...(payload.attributes || {}),
        [ATTRIBUTE_KEY]: '',
        [ATTRIBUTE_TIME_KEY]: '',
      };
      return { ...options, body: JSON.stringify(payload) };
    } catch (_) {
      return null;
    }
  }

  async function handleAddFetch(input, options) {
    await hydratePromise;
    const intent = consumeAddIntent();
    const desired = intent ? intent.accepted : false;
    const previousAcceptance = intent ? intent.previousAcceptance : state.accepted;

    try {
      await ensureAcceptance(desired, intent ? 'form-add-confirmation' : 'programmatic-quick-add');
      const response = await originalFetch(input, options);
      const payload = await response.clone().json().catch(() => null);
      const failed = !response.ok || (Number(payload?.status) >= 400);

      if (failed && previousAcceptance !== desired) {
        await persistAcceptance(previousAcceptance, 'failed-add-rollback').catch((error) => {
          console.error('[Terms Gate] A sikertelen kosárba helyezés utáni visszaállítás sikertelen.', error);
        });
      }
      return response;
    } catch (error) {
      if (previousAcceptance !== desired) {
        await persistAcceptance(previousAcceptance, 'failed-add-rollback').catch(() => undefined);
      }
      throw error;
    }
  }

  async function handleBulkUpdateFetch(input, options, updatedOptions) {
    await hydratePromise;
    const previousAcceptance = state.accepted;
    state.syncing = true;
    state.pendingAcceptance = false;
    renderState();

    try {
      const response = await originalFetch(input, updatedOptions);
      const payload = await response.clone().json().catch(() => null);
      if (!response.ok || payload?.errors) {
        throw new Error(payload?.errors || `HTTP ${response.status}`);
      }
      state.accepted = payload?.attributes?.[ATTRIBUTE_KEY] === 'true';
      dispatchAcceptanceChange('bulk-quick-add');
      return response;
    } catch (error) {
      state.accepted = previousAcceptance;
      throw error;
    } finally {
      state.syncing = false;
      state.pendingAcceptance = null;
      renderState();
    }
  }

  window.fetch = function termsGateFetch(input, options) {
    if (isAddRequest(input, options)) return handleAddFetch(input, options);

    if (requestMethod(input, options) !== 'GET' && isCartUpdateUrl(requestUrl(input))) {
      const updatedOptions = prepareBulkUpdate(options);
      if (updatedOptions) return handleBulkUpdateFetch(input, options, updatedOptions);
    }

    return originalFetch(input, options);
  };

  document.addEventListener('shopify:section:load', queueDiscovery);
  document.addEventListener('shopify:section:reorder', queueDiscovery);

  const observer = new MutationObserver(queueDiscovery);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  discoverUi();
  hydratePromise = hydrateCart();
})();
